'use client';

import React, { useEffect, useRef, useState } from 'react';

interface DropdownOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
}

export default function DropdownOverlay({ isOpen, onClose, children, triggerRef }: DropdownOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const overlayWidth = Math.min(800, viewportWidth - 32); // Max 800px width, with 16px margin on each side
    
    const newStyle = {
      position: 'fixed' as const,
      top: triggerRect.bottom + 8,
      left: Math.max(16, Math.min(triggerRect.left, viewportWidth - overlayWidth - 16)),
      width: overlayWidth,
      maxHeight: 'calc(100vh - 120px)',
      zIndex: 1000,
    };
    
    setOverlayStyle(newStyle);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleResize = () => {
      calculatePosition();
    };

    if (isOpen) {
      // Calculate initial position
      calculatePosition();
      
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize);
      };
    }
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      style={overlayStyle}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 overflow-y-auto animate-in slide-in-from-top-2 duration-200"
    >
      {children}
    </div>
  );
}